import React, { useState } from 'react';
import { Button, Text, Flex } from 'rebass';
import { LinkedInIcon, GoogleIcon, FacebookIcon } from '../icons';
import PulseLoader from 'react-spinners/PulseLoader';

interface ButtonLargeProps {
  onClick?: (e: any) => void;
  children: string | React.ReactNode;
  disabled?: boolean;
  isLoading?: boolean;
  type?: string;
  bgColor?: string;
}

export const ButtonLarge = ({
  onClick,
  children,
  disabled,
  isLoading,
  bgColor,
  ...props
}: ButtonLargeProps) => {
  const [isMouseDown, setMouseDown] = useState(false);
  const [isHover, setHover] = useState(false);

  return (
    <Button
      as="button"
      onClick={e => !isLoading && !disabled && onClick && onClick(e)}
      onMouseDown={() => setMouseDown(!isMouseDown)}
      onMouseUp={() => setMouseDown(!isMouseDown)}
      onMouseEnter={() => setHover(!isHover)}
      onMouseLeave={() => setHover(!isHover)}
      width="100%"
      height="48px"
      bg={
        disabled
          ? 'rgba(0, 0, 0, 0.12)'
          : isLoading
          ? 'blue'
          : isMouseDown || isHover
          ? 'rgba(19, 40, 76, 0.92)'
          : bgColor ?? 'blue'
      }
      sx={{
        cursor: 'pointer',
        boxShadow:
          isMouseDown || disabled || isLoading
            ? 'none'
            : '0 3px 5px -1px rgba(0, 0, 0, 0.2), 0 1px 18px 0 rgba(0, 0, 0, 0.12), 0 6px 10px 0 rgba(0, 0, 0, 0.14)',
      }}
      {...props}
    >
      <Text
        fontFamily="label"
        fontSize="16px"
        fontWeight="bold"
        letterSpacing="1.14px"
        color={disabled ? 'rgba(0, 0, 0, 0.38)' : 'rgba(255, 255, 255, 0.87)'}
      >
        {isLoading ? (
          <PulseLoader
            sizeUnit={'px'}
            size={8}
            color="rgba(255, 255, 255, 0.6)"
            loading={isLoading}
          />
        ) : (
          children
        )}
      </Text>
    </Button>
  );
};

interface ButtonSocialLoginProps {
  onClick?: () => void;
  children: string | React.ReactNode;
  disabled?: boolean;
  icon?: React.ReactNode;
  borderColor?: string;
}

export const ButtonSocialLogin = ({
  onClick,
  children,
  disabled,
  icon,
  borderColor,
  ...props
}: ButtonSocialLoginProps) => {
  const [isMouseDown, setMouseDown] = useState(false);
  const [isHover, setHover] = useState(false);

  return (
    <Button
      as="button"
      onClick={() => onClick && onClick()}
      onMouseDown={() => setMouseDown(!isMouseDown)}
      onMouseUp={() => setMouseDown(!isMouseDown)}
      onMouseEnter={() => setHover(!isHover)}
      onMouseLeave={() => setHover(!isHover)}
      width="100%"
      bg="#ffffff"
      height="40px"
      sx={{
        cursor: 'pointer',
        border: `solid 1px ${borderColor ? borderColor : '#9b9b9b'}`,
        boxShadow:
          isMouseDown || disabled
            ? 'none'
            : '0 1px 1px 0 rgba(0, 0, 0, 0.24), 0 0 1px 0 rgba(0, 0, 0, 0.12)',
      }}
      {...props}
    >
      <Flex justifyContent="center" alignItems="center">
        {icon && <Flex mr="20px">{icon}</Flex>}
        <Text
          fontFamily="'Open Sans', sans-serif"
          fontSize="16px"
          letterSpacing="0.25px"
          color="#000000"
        >
          {children}
        </Text>
      </Flex>
    </Button>
  );
};

interface ButtonSocial {
  onClick?: () => void;
  isSignUp?: boolean;
}

export const ButtonSocialLinkedIn = ({ onClick, isSignUp }: ButtonSocial) => (
  <ButtonSocialLogin
    onClick={onClick}
    borderColor="#007ebb"
    icon={<LinkedInIcon height="18px" width="20px" />}
  >
    {isSignUp ? 'Sign up' : 'Log in'} with LinkedIn
  </ButtonSocialLogin>
);

export const ButtonSocialFacebook = ({ onClick, isSignUp }: ButtonSocial) => (
  <ButtonSocialLogin
    onClick={onClick}
    borderColor="#4267b2"
    icon={<FacebookIcon height="24px" width="24px" />}
  >
    {isSignUp ? 'Sign up' : 'Log in'} with Facebook
  </ButtonSocialLogin>
);

export const ButtonSocialGoogle = ({ onClick, isSignUp }: ButtonSocial) => (
  <ButtonSocialLogin
    onClick={onClick}
    borderColor="#9b9b9b"
    icon={<GoogleIcon height="18px" width="18px" />}
  >
    {isSignUp ? 'Sign up' : 'Log in'} with Google
  </ButtonSocialLogin>
);
